import React, { useState, useEffect } from 'react';
import { X, Link as LinkIcon, Type, Palette, AlignLeft, AlignCenter, AlignRight, Maximize2 } from 'lucide-react';

interface InsertButtonModalProps {
  isOpen: boolean;
  onClose: () => void;
  onInsert: (options: {
    href: string;
    text: string;
    variant: string;
    alignment: string;
    size: string;
  }) => void;
  initialValues?: {
    href?: string;
    text?: string;
    variant?: string;
    alignment?: string;
    size?: string;
  };
}

const BUTTON_VARIANTS = [
  { value: 'primary', label: 'Primary', color: '#FF6B35', textColor: '#ffffff' },
  { value: 'secondary', label: 'Secondary', color: '#6B7280', textColor: '#ffffff' },
  { value: 'success', label: 'Success', color: '#4CAF50', textColor: '#ffffff' },
  { value: 'warning', label: 'Warning', color: '#F59E0B', textColor: '#ffffff' },
  { value: 'danger', label: 'Danger', color: '#EF4444', textColor: '#ffffff' },
];

const BUTTON_SIZES = [
  { value: 'small', label: 'S', padding: '8px 16px', fontSize: '14px' },
  { value: 'medium', label: 'M', padding: '12px 24px', fontSize: '16px' },
  { value: 'large', label: 'L', padding: '16px 32px', fontSize: '18px' },
];

const ALIGNMENTS = [
  { value: 'left', label: 'Links', icon: AlignLeft },
  { value: 'center', label: 'Midden', icon: AlignCenter },
  { value: 'right', label: 'Rechts', icon: AlignRight },
];

export function InsertButtonModal({ isOpen, onClose, onInsert, initialValues }: InsertButtonModalProps) {
  const [href, setHref] = useState(initialValues?.href || '');
  const [text, setText] = useState(initialValues?.text || 'Lees meer');
  const [variant, setVariant] = useState(initialValues?.variant || 'primary');
  const [alignment, setAlignment] = useState(initialValues?.alignment || 'left');
  const [size, setSize] = useState(initialValues?.size || 'medium');
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen) {
      setHref(initialValues?.href || '');
      setText(initialValues?.text || 'Lees meer');
      setVariant(initialValues?.variant || 'primary');
      setAlignment(initialValues?.alignment || 'left');
      setSize(initialValues?.size || 'medium');
      setError('');
    }
  }, [isOpen, initialValues]);

  if (!isOpen) return null;

  const validateUrl = (url: string): boolean => {
    if (!url.trim()) return false;
    try {
      new URL(url);
      return true;
    } catch {
      return url.startsWith('/') || url.startsWith('#');
    }
  };

  const handleInsert = () => {
    setError('');

    if (!text.trim()) {
      setError('Voer button tekst in');
      return;
    }

    if (!href.trim()) {
      setError('Voer een URL in');
      return;
    }

    if (!validateUrl(href)) {
      setError('Voer een geldige URL in (bijv. https://example.com)');
      return;
    }

    onInsert({ href, text, variant, alignment, size });
    onClose();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && e.metaKey) {
      handleInsert();
    }
    if (e.key === 'Escape') {
      onClose();
    }
  };


  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center sm:justify-center justify-end z-50 p-0 sm:p-4">
      <div className="bg-white rounded-t-2xl sm:rounded-lg shadow-xl max-w-md w-full max-h-[90vh] sm:max-h-[85vh] overflow-y-auto" onKeyDown={handleKeyDown}>
        <div className="flex items-center justify-between p-4 sm:p-4 border-b border-gray-200 bg-white sticky top-0 z-10">
          <h2 className="text-lg sm:text-lg font-bold text-[#231F20] flex items-center gap-2">
            <Palette className="w-5 h-5 text-[#FF6B35]" />
            {initialValues?.href ? 'Button Bewerken' : 'Button Toevoegen'}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 active:text-gray-700 transition-colors p-2 -m-2 touch-manipulation"
          >
            <X className="w-6 h-6 sm:w-5 sm:h-5" />
          </button>
        </div>

        <div className="p-4 sm:p-4 space-y-5 sm:space-y-4">
          <div>
            <label htmlFor="button-text" className="block text-base sm:text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
              <Type className="w-5 h-5 sm:w-4 sm:h-4" />
              Button Tekst <span className="text-red-500">*</span>
            </label>
            <input
              id="button-text"
              type="text"
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="Bijv. Lees meer, Bekijk artikel, Download nu"
              className="input text-base sm:text-sm min-h-[48px] sm:min-h-[40px]"
              autoFocus
            />
          </div>

          <div>
            <label htmlFor="button-url" className="block text-base sm:text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
              <LinkIcon className="w-5 h-5 sm:w-4 sm:h-4" />
              URL <span className="text-red-500">*</span>
            </label>
            <input
              id="button-url"
              type="url"
              value={href}
              onChange={(e) => setHref(e.target.value)}
              placeholder="https://voorbeeld.nl/artikel"
              className="input text-base sm:text-sm min-h-[48px] sm:min-h-[40px]"
            />
          </div>

          <div>
            <label className="block text-base sm:text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
              <Palette className="w-5 h-5 sm:w-4 sm:h-4" />
              Kleur
            </label>
            <div className="flex gap-3 sm:gap-2">
              {BUTTON_VARIANTS.map((v) => (
                <button
                  key={v.value}
                  onClick={() => setVariant(v.value)}
                  className={`w-12 h-12 sm:w-8 sm:h-8 rounded-full border-2 transition-all touch-manipulation ${
                    variant === v.value
                      ? 'border-[#FF6B35] ring-4 sm:ring-2 ring-[#FF6B35] ring-opacity-50'
                      : 'border-gray-200 hover:border-gray-300 active:border-gray-400'
                  }`}
                  style={{ backgroundColor: v.color }}
                  title={v.label}
                />
              ))}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-base sm:text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                <Maximize2 className="w-5 h-5 sm:w-4 sm:h-4" />
                Grootte
              </label>
              <div className="flex gap-2">
                {BUTTON_SIZES.map((s) => (
                  <button
                    key={s.value}
                    onClick={() => setSize(s.value)}
                    className={`flex-1 h-12 sm:h-8 rounded border-2 transition-all text-sm sm:text-xs font-semibold touch-manipulation ${
                      size === s.value
                        ? 'border-[#FF6B35] bg-orange-50 text-[#FF6B35]'
                        : 'border-gray-200 hover:border-gray-300 active:border-gray-400 text-gray-700'
                    }`}
                    title={s.label}
                  >
                    {s.label}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-base sm:text-sm font-medium text-gray-700 mb-2">
                Uitlijning
              </label>
              <div className="flex gap-2">
                {ALIGNMENTS.map((a) => {
                  const Icon = a.icon;
                  return (
                    <button
                      key={a.value}
                      onClick={() => setAlignment(a.value)}
                      className={`flex-1 h-12 sm:h-8 rounded border-2 transition-all flex items-center justify-center touch-manipulation ${
                        alignment === a.value
                          ? 'border-[#FF6B35] bg-orange-50 text-[#FF6B35]'
                          : 'border-gray-200 hover:border-gray-300 active:border-gray-400 text-gray-700'
                      }`}
                      title={a.label}
                    >
                      <Icon className="w-5 h-5 sm:w-4 sm:h-4" />
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm sm:text-sm text-red-800">
              {error}
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-3 p-4 sm:p-4 border-t border-gray-200 bg-gray-50 sticky bottom-0">
          <button onClick={onClose} className="btn-secondary min-h-[48px] sm:min-h-[40px] px-6 text-base sm:text-sm touch-manipulation">
            Annuleren
          </button>
          <button onClick={handleInsert} className="btn-primary min-h-[48px] sm:min-h-[40px] px-6 text-base sm:text-sm touch-manipulation">
            {initialValues?.href ? 'Bijwerken' : 'Invoegen'}
          </button>
        </div>
      </div>
    </div>
  );
}
